import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import { Search, RefreshCw, TrendingUp, HelpCircle, Link2, DollarSign, Beaker, Sparkles } from "lucide-react";

// A small, self-contained panel that fetches SERP + keyword research for
// a keyword and renders it read-only for the editor. When the user
// clicks "Generate Brief", we call Claude 4.6 via the auto-brief endpoint
// and hand back a full brief dict via `onBriefGenerated`. Zero state leaks
// back into the brief form unless the user explicitly opts in.
export default function SeoResearchPanel({ primaryKeyword, secondaryKeywords, country, language, targetAudience, onBriefGenerated }) {
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);

  React.useEffect(() => {
    seoApi.get("/seo/research/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  async function run(useCache = true) {
    const kw = (primaryKeyword || "").trim();
    if (!kw || kw.length < 2) {
      toast.error("Add a primary keyword first — research needs at least 2 characters.");
      return;
    }
    setLoading(true); setError(null);
    try {
      const location_code = _countryToLocation(country);
      const language_code = _languageToCode(language);
      const { data: b } = await seoApi.post("/seo/research/bundle", {
        keyword: kw,
        location_code, language_code,
        use_cache: useCache,
        include_related: true, include_ideas: true,
      });
      setData(b);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.message || e?.message || "Research failed.";
      setError(msg);
      toast.error(`Research: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  async function generateBrief() {
    const kw = (primaryKeyword || "").trim();
    if (!kw || kw.length < 2) {
      toast.error("Add a primary keyword first.");
      return;
    }
    if (!onBriefGenerated) return;
    setBuilding(true); setError(null);
    try {
      const { data: resp } = await seoApi.post("/seo/briefs/auto-build", {
        primary_keyword: kw,
        country: country || "United States",
        language: language || "English",
        target_audience_hint: targetAudience || null,
        use_cache: true,
      });
      const summary = resp?.research_summary || {};
      toast.success(
        `Brief generated · ${resp?.brief?.outline?.length || 0} sections · ` +
        `${resp?.brief?.faq_topics?.length || 0} FAQ topics · ` +
        `$${(resp?.cost_usd || 0).toFixed(3)}`
      );
      onBriefGenerated(resp.brief, { summary, cost_usd: resp.cost_usd });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "Failed to generate brief.");
      setError(msg);
      toast.error(`Generate Brief: ${msg}`);
    } finally {
      setBuilding(false);
    }
  }

  return (
    <Card className="border-dashed border-neutral-300" data-testid="seo-research-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Search className="w-4 h-4" />
          SEO research
          {health?.local_stub && (
            <span className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">
              <Beaker className="w-3 h-3" /> local stub — no cost
            </span>
          )}
          {health && !health.local_stub && health.sandbox && (
            <span className="inline-flex items-center gap-1 text-[10px] font-normal text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-sm">
              sandbox
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={() => run(true)} disabled={loading || building}
                   data-testid="research-run-btn">
            {loading ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
            {data ? "Re-run research" : "Run research"}
          </Button>
          {data && (
            <Button size="sm" variant="outline" onClick={() => run(false)}
                     disabled={loading || building} data-testid="research-force-fresh">
              Force fresh
            </Button>
          )}
          {onBriefGenerated && (
            <Button size="sm" variant="default"
                     className="bg-emerald-700 hover:bg-emerald-800"
                     onClick={generateBrief}
                     disabled={loading || building || !(primaryKeyword || "").trim()}
                     data-testid="generate-brief-btn">
              {building ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              Generate Brief
            </Button>
          )}
          {data && (
            <span className="text-xs text-neutral-500">
              {data.serp?.served_from_cache
                ? <>Served from cache · <span className="text-emerald-700">$0.00</span></>
                : <>Fresh · <span className="text-neutral-800">${_estimatedBundleCost(data).toFixed(3)}</span></>}
            </span>
          )}
        </div>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        {data && <ResearchResults data={data} />}
      </CardContent>
    </Card>
  );
}

function ResearchResults({ data }) {
  const serp = data.serp || {};
  const vol = data.keyword_volume?.keyword_metrics?.[0];
  const related = data.related?.related_keywords || [];
  const ideas = data.ideas?.ideas || [];
  const questions = data.ideas?.questions || [];
  const paa = serp.people_also_ask || [];
  const relatedSearches = serp.related_searches || [];
  const top10 = serp.top10 || [];
  const signals = serp.editorial_signals || {};
  const fs = serp.featured_snippet;

  return (
    <div className="space-y-4 pt-2">
      {/* KPIs row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi icon={<TrendingUp className="w-3.5 h-3.5" />} label="Volume/mo"
             value={vol?.search_volume ? vol.search_volume.toLocaleString() : "—"} />
        <Kpi icon={<DollarSign className="w-3.5 h-3.5" />} label="CPC"
             value={vol?.cpc_usd != null ? `$${vol.cpc_usd.toFixed(2)}` : "—"} />
        <Kpi icon={<Link2 className="w-3.5 h-3.5" />} label="Competition"
             value={vol?.competition || "—"} />
        <Kpi icon={<HelpCircle className="w-3.5 h-3.5" />} label="Results in SERP"
             value={String(serp.results_count ?? "—")} />
      </div>

      {/* Editorial signals row */}
      <div className="text-xs text-neutral-600 border-t border-neutral-200 pt-2">
        <span className="font-medium text-neutral-800">Competitor signals · </span>
        avg title {signals.avg_title_len || 0} chars,
        avg description {signals.avg_description_len || 0} chars,
        {signals.unique_domains || 0} unique domains,
        keyword in titles {signals.keyword_in_titles_pct || 0}% · in descriptions {signals.keyword_in_descriptions_pct || 0}%.
      </div>

      {/* Featured snippet */}
      {fs && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
          <div className="font-medium text-amber-900 mb-0.5">Featured snippet · {fs.domain}</div>
          <div className="text-amber-800">{fs.description || fs.title}</div>
        </div>
      )}

      {/* Top-10 */}
      <div>
        <div className="text-xs font-medium text-neutral-800 mb-1.5">Top-10 organic</div>
        <ol className="space-y-1 text-xs" data-testid="research-top10">
          {top10.map((r, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-neutral-400 w-4 text-right">{i + 1}.</span>
              <div className="min-w-0">
                <div className="truncate">{r.title || "(untitled)"}</div>
                <div className="text-neutral-500 truncate">{r.domain}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* People Also Ask */}
      {paa.length > 0 && (
        <Group title="People Also Ask" testId="research-paa">
          <ul className="text-xs text-neutral-700 space-y-1 list-disc pl-4">
            {paa.slice(0, 8).map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </Group>
      )}

      {/* Related searches (bottom of SERP) */}
      {relatedSearches.length > 0 && (
        <Group title="Related searches (bottom of SERP)">
          <div className="flex flex-wrap gap-1 text-xs">
            {relatedSearches.slice(0, 12).map((s, i) => (
              <span key={i} className="bg-neutral-100 text-neutral-800 rounded px-2 py-0.5">{s}</span>
            ))}
          </div>
        </Group>
      )}

      {/* Related keywords (DFS Labs) */}
      {related.length > 0 && (
        <Group title={`Semantic related (${related.length})`} testId="research-related">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-neutral-500">
                <th className="text-left font-normal py-0.5">Keyword</th>
                <th className="text-right font-normal py-0.5">Volume</th>
                <th className="text-right font-normal py-0.5">KD</th>
                <th className="text-left font-normal py-0.5 pl-2">Intent</th>
              </tr>
            </thead>
            <tbody>
              {related.slice(0, 12).map((r, i) => (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="py-0.5 pr-2">{r.keyword}</td>
                  <td className="py-0.5 text-right">{r.search_volume?.toLocaleString() ?? "—"}</td>
                  <td className="py-0.5 text-right">{r.keyword_difficulty ?? "—"}</td>
                  <td className="py-0.5 pl-2 text-neutral-600">{r.search_intent || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Group>
      )}

      {/* Long-tail ideas + questions */}
      {ideas.length > 0 && (
        <Group title={`Long-tail ideas (${ideas.length})`}>
          <div className="flex flex-wrap gap-1 text-xs">
            {ideas.slice(0, 20).map((r, i) => (
              <span key={i} className={`rounded px-2 py-0.5 border ${r.is_question ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-neutral-50 border-neutral-200 text-neutral-800"}`}>
                {r.keyword}
                {r.search_volume != null && <span className="text-neutral-400 ml-1">· {r.search_volume}</span>}
              </span>
            ))}
          </div>
        </Group>
      )}
      {questions.length > 0 && (
        <Group title={`Questions (${questions.length})`}>
          <ul className="text-xs text-neutral-700 space-y-1 list-disc pl-4">
            {questions.slice(0, 10).map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </Group>
      )}
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="bg-neutral-50 border border-neutral-200 rounded p-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-neutral-500">
        {icon} {label}
      </div>
      <div className="text-sm font-medium text-neutral-900 mt-0.5">{value}</div>
    </div>
  );
}

function Group({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs font-medium text-neutral-800 mb-1.5">{title}</div>
      {children}
    </div>
  );
}

// ── country / language mapping (client-side, subset used in the app) ──
const COUNTRY_TO_LOCATION = {
  "united states": 2840, "us": 2840, "usa": 2840,
  "united kingdom": 2826, "uk": 2826, "great britain": 2826,
  "germany": 2276, "france": 2250, "italy": 2380, "spain": 2724,
  "netherlands": 2528, "canada": 2124, "australia": 2036,
  "russia": 2643, "russian federation": 2643,
  "china": 2156, "japan": 2392, "india": 2356,
  "vietnam": 2704, "indonesia": 2360, "singapore": 2702,
  "brazil": 2076, "mexico": 2484,
};

function _countryToLocation(country) {
  const k = (country || "").trim().toLowerCase();
  return COUNTRY_TO_LOCATION[k] || 2840;  // default USA
}

const LANGUAGE_TO_CODE = {
  english: "en", russian: "ru", german: "de", french: "fr", spanish: "es",
  italian: "it", portuguese: "pt", dutch: "nl", chinese: "zh", japanese: "ja",
  vietnamese: "vi", indonesian: "id", arabic: "ar",
};

function _languageToCode(language) {
  const k = (language || "").trim().toLowerCase();
  return LANGUAGE_TO_CODE[k] || "en";
}

function _estimatedBundleCost(bundle) {
  // Rough live cost, matches DataForSEO Live pricing at time of writing.
  let c = 0;
  if (bundle?.serp) c += 0.002;
  if (bundle?.keyword_volume) c += 0.05;
  if (bundle?.related?.related_keywords) c += 0.05;
  if (bundle?.ideas?.ideas) c += 0.05;
  return c;
}
